import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template10Props {
  data: ResumeData;
}

export const Template10: React.FC<Template10Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      {/* Split Layout */}
      <div className="grid grid-cols-5 gap-0 min-h-screen">
        {/* Left Column - Dark */}
        <div className="col-span-2 bg-gray-900 text-white p-8">
          {/* Profile */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{data.personalInfo.name}</h1>
            <h2 className="text-lg text-violet-300 mb-6">{data.personalInfo.title}</h2>
            
            <div className="w-16 h-1 bg-violet-400 mb-6"></div>
            
            <div className="space-y-2 text-sm">
              <p className="flex items-center">
                <span className="w-4 h-4 bg-violet-400 rounded-full mr-3 flex-shrink-0"></span>
                {data.personalInfo.email}
              </p>
              <p className="flex items-center">
                <span className="w-4 h-4 bg-violet-400 rounded-full mr-3 flex-shrink-0"></span>
                {data.personalInfo.phone}
              </p>
              <p className="flex items-center">
                <span className="w-4 h-4 bg-violet-400 rounded-full mr-3 flex-shrink-0"></span>
                {data.personalInfo.location}
              </p>
              {data.personalInfo.linkedin && (
                <p className="flex items-center">
                  <span className="w-4 h-4 bg-violet-400 rounded-full mr-3 flex-shrink-0"></span>
                  {data.personalInfo.linkedin}
                </p>
              )}
              {data.personalInfo.website && (
                <p className="flex items-center">
                  <span className="w-4 h-4 bg-violet-400 rounded-full mr-3 flex-shrink-0"></span>
                  {data.personalInfo.website}
                </p>
              )}
            </div>
          </div>

          {/* Skills */}
          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4 text-violet-300">SKILLS</h3>
            <div className="space-y-4">
              {data.skills.map((skill, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">{skill}</span>
                    <span className="text-xs text-gray-400">Expert</span>
                  </div>
                  <div className="w-full bg-gray-700 h-2 rounded-full">
                    <div className="bg-gradient-to-r from-violet-400 to-purple-500 h-2 rounded-full w-5/6"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Education */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-violet-300">EDUCATION</h3>
            <EducationFormatter 
              education={data.education} 
              className="text-white text-sm"
            />
          </div>
        </div>

        {/* Right Column - Light */}
        <div className="col-span-3 p-8 bg-gray-50">
          {/* Summary */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-violet-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-white font-bold">👤</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">ABOUT ME</h3>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-violet-500">
              <p className="text-gray-700 leading-relaxed">{data.summary}</p>
            </div>
          </section>

          {/* Experience */}
          <section>
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-violet-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-white font-bold">💼</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">EXPERIENCE</h3>
            </div>
            
            <div className="space-y-6">
              {data.experience.map((job, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{job.title}</h4>
                      <p className="text-violet-600 font-semibold">{job.company}</p>
                      <p className="text-sm text-gray-600">{job.location}</p>
                    </div>
                    <div className="bg-violet-100 text-violet-700 px-3 py-2 rounded-lg text-sm font-bold">
                      {job.dates}
                    </div>
                  </div>
                  
                  <ul className="space-y-2">
                    {job.bullets.map((bullet, bulletIndex) => (
                      <li key={bulletIndex} className="flex items-start">
                        <div className="w-2 h-2 bg-violet-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};